import { Component } from '@theme/component';
import { isClickedOutside, isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} trigger - The button element.
 * @property {HTMLAnchorElement} productLink - The product link element.
 *
 * @extends Component<Refs>
 */

export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];
  /** @type {(() => void) | null} */
  #pointerenterHandler = null;  
  _ignoreNextClick = false;
  /** @type {Promise<boolean> | null} */
  #cardPromise = null;
  /** @type {IntersectionObserver | null} */
  #cardObserver = null;

  /**
   * Fetch the product card (sections/hotspot-product-card.liquid) through the
   * Section Rendering API and inject it into the dialog. The homepage renders
   * only a shell: a product setting on the block made Shopify load every
   * hotspot's product before the section rendered, which was the homepage's
   * server-time regression (Fastr, Oct 2026). Runs once per hotspot; callers
   * can await the promise or fire and forget.
   * @returns {Promise<boolean>} true once the card is in the DOM
   */
  loadCard() {
    if (this.#cardPromise) return this.#cardPromise;
    const src = this.dataset.hotspotCardSrc;
    const card = this.querySelector('.hotspot-dialog__card--product');
    if (!src || !(card instanceof HTMLElement)) return Promise.resolve(false);

    card.dataset.hotspotCardState = 'loading';
    this.#cardPromise = fetch(src, { headers: { Accept: 'text/html' }, priority: 'low' })
      .then((response) => {
        if (!response.ok) throw new Error(`hotspot card ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fetched = doc.querySelector('[data-hotspot-card]');
        if (!(fetched instanceof HTMLElement)) throw new Error('hotspot card markup missing');

        const label = (this.dataset.productButtonLabel || '').trim();
        const button = fetched.querySelector('[data-hotspot-button-label]');
        if (label && button) button.textContent = label;

        card.replaceChildren(...Array.from(fetched.childNodes));
        card.dataset.hotspotCardState = 'ready';

        const title = (fetched.dataset.hotspotCardTitle || '').trim();
        const trigger = this.refs?.trigger;
        if (title && trigger instanceof HTMLElement) {
          trigger.setAttribute('aria-label', title);
        }

        this.#preloadedSwatches = false;
        if (this.refs?.dialog?.open) {
          this.#preloadSwatchImages();
          this.#handleViewportChange();
        }
        return true;
      })
      .catch(() => {
        // Keep the dialog useful: a plain link to the product.
        const url = this.dataset.productUrl || '';
        if (url) {
          const link = document.createElement('a');
          link.href = url;
          link.className = 'hotspot-dialog__button hotspot-dialog__button--link hotspot-dialog__view-link';
          link.textContent = (this.dataset.productButtonLabel || 'VIEW PRODUCT').trim();
          card.replaceChildren(link);
        }
        card.dataset.hotspotCardState = 'error';
        this.#cardPromise = null;
        return false;
      });
    return this.#cardPromise;
  }

  /**
   * Fetch the card ahead of interaction once the hotspot scrolls near the
   * viewport, so a hover or tap usually finds it ready.
   * @returns {void}
   */
  #observeCardViewport() {
    if (!this.dataset.hotspotCardSrc || this.#cardObserver || typeof IntersectionObserver !== 'function') return;
    this.#cardObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#disconnectCardObserver();
        const run = () => void this.loadCard();
        if (typeof requestIdleCallback === 'function') {
          requestIdleCallback(run, { timeout: 1500 });
        } else {
          setTimeout(run, 300);
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.#cardObserver.observe(this);
  }

  #disconnectCardObserver() {
    if (this.#cardObserver) {
      this.#cardObserver.disconnect();
      this.#cardObserver = null;
    }
  }
  /** @type {() => void} */
  #handleViewportChange = () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    const placed = this.#calculateDialogPlacement();
    if (placed && typeof placed.then === 'function') {
      placed.then(() => this.#anchorDialogBottom());
    } else {
      this.#anchorDialogBottom();
    }
  };

  /**
   * Anchor the open dialog by its BOTTOM edge instead of its top. Hover
   * previews can legitimately change the card's height (the red discount
   * line appears for some colors); bottom-anchored, that growth extends the
   * card upward, so the swatch row under the cursor never moves.
   * @returns {void}
   */
  #anchorDialogBottom() {
    const { dialog } = this.refs;
    if (!dialog.open || dialog.dataset.placement === 'mobile') return;
    const topPx = parseFloat(dialog.style.top);
    if (Number.isNaN(topPx)) return;
    const bottom = this.offsetHeight - topPx - dialog.offsetHeight;
    dialog.style.bottom = `${Math.round(bottom)}px`;
    dialog.style.top = 'auto';
  }
  /** @param {MouseEvent} e */
  #handleDialogBackdropClick = (e) => {
    const { dialog } = this.refs;
    if (e.target === dialog) {
      this.closeDialog();
    }
  };
  timer = /** @type {number | null} */ (null);
  closeTimer = /** @type {number | null} */ (null);

  /**
   * Cancel a pending grace-period close (pointer returned to trigger or dialog).
   * @returns {void}
   */
  #cancelScheduledClose = () => {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  };

  /**
   * Close after a grace period so the pointer can travel across the gap
   * between the trigger and the dialog without dismissing it.
   * @returns {void}
   */
  #scheduleClose = () => {
    this.#cancelScheduledClose();
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.closeDialog();
    }, 300);
  };

  getHotspotProductLink() {
    return /** @type {HTMLAnchorElement | null} */ (
      this.querySelector('.hotspot-dialog__product-title-link, .hotspot-dialog__product-image-link, .hotspot-dialog__swatch-more')
    );
  }

  connectedCallback() {
    super.connectedCallback();

    // Set up initial event listeners based on current breakpoint
    this.#handleBreakpointChange();

    // Listen for breakpoint changes
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);

    this.#observeCardViewport();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up listeners
    this.#removeDesktopListeners();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
    this.#disconnectCardObserver();
  }

  /**
   * Set up desktop event listeners (hover)
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    /** @type {() => void} */
    const pointerenterHandler = () => {
      this.#cancelScheduledClose();
      void this.loadCard();
      if (dialog.open) return;

      this.timer = setTimeout(() => {
        this.showDialog();
      }, 120);
      // Add pointerleave listener when entering trigger
      trigger.addEventListener('pointerleave', this.#handlePointerLeave);
    };

    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
  }

  /**
   * Remove desktop event listeners from trigger
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#pointerenterHandler = null;
    }

    // Clear any pending timers
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.#cancelScheduledClose();
  }

  /**
   * Handle breakpoint changes
   * @returns {void}
   */
  #handleBreakpointChange = () => {
    // Remove existing listeners
    this.#removeDesktopListeners();

    // Set up desktop hover listeners only (mobile uses on:click in template)
    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  /**
   * Calculate the placement of the dialog.
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;

    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) {
      return;
    }

    // Spacing constants
    const BUTTON_GAP = 10; // Gap between button and dialog
    const CONTAINER_GAP = 10; // Gap from container edges
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    // Get container bounds
    const containerRect = hotspotsContainer?.getBoundingClientRect();

    // Mobile behavior: bottom sheet pinned to viewport bottom
    if (isMobileBreakpoint()) {
      const leftInset = 0;
      const rightInset = 0;
      const bottomInset = 0;

      dialog.dataset.placement = 'mobile';
      dialog.style.setProperty('--mobile-dialog-left', `${leftInset}px`);
      dialog.style.setProperty('--mobile-dialog-right', `${rightInset}px`);
      dialog.style.setProperty('--mobile-dialog-bottom', `${bottomInset}px`);
      dialog.style.removeProperty('--dialog-vertical-offset');

      return Promise.resolve();
    }

    // Get button dimensions
    const triggerRect = trigger.getBoundingClientRect();

    // To get dialog dimensions, we need to temporarily show it invisibly
    // Show dialog invisibly to measure it
    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    // Reset dialog state
    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');
    // Calculate button position relative to container
    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const hostRect = this.getBoundingClientRect();
    const hostLeft = hostRect.left - containerRect.left;
    const hostTop = hostRect.top - containerRect.top;
    const centerX = buttonLeft + triggerRect.width / 2;
    const centerY = buttonTop + triggerRect.height / 2;

    /** @type {Array<{x: 'left' | 'right' | 'center', y: 'top' | 'bottom', left: number, top: number}>} */
    const preferredPlacements = [
      {
        x: 'left',
        y: 'bottom',
        left: centerX - dialogWidth,
        top: centerY,
      },
      {
        x: 'right',
        y: 'bottom',
        left: centerX,
        top: centerY,
      },
      {
        x: 'left',
        y: 'top',
        left: centerX - dialogWidth,
        top: centerY - dialogHeight,
      },
      {
        x: 'right',
        y: 'top',
        left: centerX,
        top: centerY - dialogHeight,
      },
      {
        x: 'center',
        y: 'bottom',
        left: centerX - dialogWidth / 2,
        top: centerY,
      },
      {
        x: 'center',
        y: 'top',
        left: centerX - dialogWidth / 2,
        top: centerY - dialogHeight,
      },
    ];

    /** @param {{ left: number, top: number }} placement */
    const fitsInContainer = ({ left, top }) => {
      return (
        left >= CONTAINER_GAP &&
        top >= CONTAINER_GAP &&
        left + dialogWidth <= containerRect.width - CONTAINER_GAP &&
        top + dialogHeight <= containerRect.height - CONTAINER_GAP
      );
    };

    const chosenPlacement = preferredPlacements.find(fitsInContainer) ?? preferredPlacements[0];

    if (!chosenPlacement) {
      return;
    }

    const clampedLeft = Math.min(
      Math.max(chosenPlacement.left, CONTAINER_GAP),
      containerRect.width - dialogWidth - CONTAINER_GAP,
    );
    const clampedTop = Math.min(
      Math.max(chosenPlacement.top, CONTAINER_GAP),
      containerRect.height - dialogHeight - CONTAINER_GAP,
    );

    dialog.dataset.placement = `${chosenPlacement.x},${chosenPlacement.y}`;
    dialog.style.left = `${Math.round(clampedLeft - hostLeft)}px`;
    dialog.style.top = `${Math.round(clampedTop - hostTop)}px`;
    dialog.style.right = 'auto';
    dialog.style.bottom = 'auto';
    dialog.style.removeProperty('--dialog-vertical-offset');

    // Return a promise that resolves after a few ticks to ensure styles are applied
    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * Handle pointer leave.
   * @param {PointerEvent} e - The event.
   * @returns {void}
   */
  #handlePointerLeave = (e) => {
    const { dialog, trigger } = this.refs;

    // Clear open timer if leaving trigger before dialog opens
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (!dialog.open) return;

    const isLeavingTrigger = e.target === trigger;
    const isLeavingDialog = e.target === dialog;
    const isGoingToDialog =
      e.relatedTarget === dialog ||
      (e.relatedTarget instanceof Element && e.relatedTarget.closest('dialog') === dialog);
    const isGoingToTrigger = e.relatedTarget === trigger;

    if ((isLeavingTrigger && !isGoingToDialog) || (isLeavingDialog && !isGoingToTrigger)) {
      this.#scheduleClose();
    }
  };

  /**
   * Handle hotspot click: toggle dialog open/close.
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    if (this._ignoreNextClick) {
        return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      void this.loadCard();
      this.showDialog();
    }
  };

  /**
   * Handle dedicated close button click.
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleCloseButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.closeDialog();
  };

  /** Warm the browser cache so hover swaps paint instantly. */
  #preloadedSwatches = false;
  #preloadSwatchImages() {
    if (this.#preloadedSwatches) return;
    this.#preloadedSwatches = true;
    this.querySelectorAll('.hotspot-dialog__swatch-button[data-variant-image-url]').forEach((btn) => {
      const url = btn instanceof HTMLElement ? btn.dataset.variantImageUrl : null;
      if (!url) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    });
  }

  showDialog = async () => {
    const { dialog } = this.refs;

    this.#preloadSwatchImages();

    const otherOpenHotspots = Array.from(document.querySelectorAll('product-hotspot-component')).filter(
      /** @returns {element is ProductHotspotComponent} */
      (element) =>
        element !== this &&
        element instanceof ProductHotspotComponent &&
        element.refs?.dialog instanceof HTMLDialogElement &&
        element.refs.dialog.open,
    );

    await Promise.allSettled(otherOpenHotspots.map((hotspot) => hotspot.closeDialog()));

   
    const isMobile = isMobileBreakpoint();

    if (isMobile) {      
      this._ignoreNextClick = true;
      setTimeout(() => {
        this._ignoreNextClick = false;
      }, 350);      
      await new Promise((r) => setTimeout(r, 80));
    }

    await this.#calculateDialogPlacement();
    dialog.dataset.showing = 'true';

    // On mobile use showModal to create a native backdrop and modal behavior
    if (dialog.dataset.placement === 'mobile' && typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {      
      if (typeof dialog.show === 'function') dialog.show();
    }    
    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);
    window.addEventListener('resize', this.#handleViewportChange);
    window.addEventListener('scroll', this.#handleViewportChange, { passive: true });    
    if (!isMobile) {
      try {
        dialog.addEventListener('pointerleave', this.#handlePointerLeave);
        dialog.addEventListener('pointerenter', this.#cancelScheduledClose);
      } catch (err) {}
      // Floor the dialog at its opened size: it may grow for a hover preview
      // (red discount line needs width to stay on one line, and a row of
      // height) but never shrink below the at-rest size, so short content
      // can't make the card jitter smaller.
      const rect = dialog.getBoundingClientRect();
      if (rect.width && rect.height) {
        dialog.style.minWidth = `${Math.ceil(rect.width)}px`;
        dialog.style.minHeight = `${Math.ceil(rect.height)}px`;
      }
      // Growth (red discount line appearing) extends the card upward,
      // keeping the swatch row still under the cursor.
      this.#anchorDialogBottom();
    }
    try { this.setAttribute('data-dialog-open', 'true'); } catch (err) {}
    try { dialog.addEventListener('click', this.#handleDialogBackdropClick); } catch (err) {}
    try { dialog.addEventListener('cancel', this.closeDialog); } catch (err) {}
  };

  /**
   * Close the dialog.
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog, trigger } = this.refs;

    this.#cancelScheduledClose();

    if (!dialog.open) {
      return;
    }
    dialog.dataset.closing = 'true';
    dialog.close();
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    window.removeEventListener('resize', this.#handleViewportChange);
    window.removeEventListener('scroll', this.#handleViewportChange);
    // Remove pointerleave listeners when closing
    try { dialog.removeEventListener('pointerleave', this.#handlePointerLeave); } catch (err) {}
    try { dialog.removeEventListener('pointerenter', this.#cancelScheduledClose); } catch (err) {}
    try { trigger.removeEventListener('pointerleave', this.#handlePointerLeave); } catch (err) {}
    try {
      const triggerEl = trigger;
      if (triggerEl instanceof HTMLElement) {
        triggerEl.style.removeProperty('pointer-events');
        triggerEl.style.removeProperty('z-index');
        const icon = triggerEl.querySelector('.hotspot-icon');
        if (icon instanceof HTMLElement) icon.style.removeProperty('opacity');
      }
    } catch (err) {}
    // we need to use a data-attribute to keep transition-behavior working only when open
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
      dialog.style.removeProperty('left');
      dialog.style.removeProperty('top');
      dialog.style.removeProperty('right');
      dialog.style.removeProperty('bottom');
      dialog.style.removeProperty('min-width');
      dialog.style.removeProperty('min-height');
      dialog.style.removeProperty('--dialog-vertical-offset');
    }
    // remove backdrop handlers and CSS hook
    try { dialog.removeEventListener('click', this.#handleDialogBackdropClick); } catch (err) {}
    try { dialog.removeEventListener('cancel', this.closeDialog); } catch (err) {}
    try { this.removeAttribute('data-dialog-open'); } catch (err) {}
  };

  /**
   * Light dismiss the dialog.
   * @param {MouseEvent} event - The event.
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;

    if (this._ignoreNextClick) {
      return;
    }

    if (event.composedPath().includes(dialog)) {
      return;
    }

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

// Register custom element
customElements.define('product-hotspot-component', ProductHotspotComponent);

// Monotonic token per card: only the newest hover's image is allowed to paint,
// so slow responses arriving out of order can never show a stale color.
const imageSwapTokens = new WeakMap();

const applyHotspotSwatchState = (button, options = {}) => {
  const { commitActive = true } = options;
  if (!(button instanceof HTMLElement) || !button.dataset.variantUrl) return;

  const hotspot = button.closest('product-hotspot-component');
  const card = button.closest('.hotspot-dialog__card--product');
  if (!(hotspot instanceof ProductHotspotComponent) || !(card instanceof HTMLElement)) return;

  const variantUrl = button.dataset.variantUrl;
  const imageUrl = button.dataset.variantImageUrl || null;

  // Skip redundant re-applies (pointerover bubbles from the button's children),
  // which otherwise re-swap the image and rebuild the price DOM on every move.
  if (card.dataset.previewUrl === variantUrl) {
    if (commitActive) {
      card.querySelectorAll('.hotspot-dialog__swatch-button').forEach((swatchButton) => {
        swatchButton.setAttribute('aria-pressed', swatchButton === button ? 'true' : 'false');
        swatchButton.closest('.hotspot-dialog__swatch-item')?.classList.toggle('is-active', swatchButton === button);
      });
    }
    return;
  }
  card.dataset.previewUrl = variantUrl;

  const imageLink = card.querySelector('.hotspot-dialog__product-image-link');
  if (imageLink instanceof HTMLAnchorElement) {
    imageLink.href = variantUrl;
  }

  const titleLink = card.querySelector('.hotspot-dialog__product-title-link');
  if (titleLink instanceof HTMLAnchorElement) {
    titleLink.href = variantUrl;
  }

  const productImage = card.querySelector('.hotspot-dialog__product-image');
  if (productImage instanceof HTMLImageElement && imageUrl && productImage.getAttribute('src') !== imageUrl) {
    const token = (imageSwapTokens.get(card) || 0) + 1;
    imageSwapTokens.set(card, token);

    const loader = new Image();
    loader.decoding = 'async';
    loader.src = imageUrl;

    // Swap only once the new image is fully decoded: the paint is atomic, so
    // the previous color is never shown half-replaced (no ghosting), and
    // superseded hovers are dropped instead of flashing through.
    const applyImage = () => {
      if (imageSwapTokens.get(card) !== token) return;
      if (card.dataset.previewUrl !== variantUrl) return;
      productImage.src = imageUrl;
      productImage.srcset = imageUrl;
    };

    if (typeof loader.decode === 'function') {
      loader.decode().then(applyImage).catch(applyImage);
    } else if (loader.complete) {
      applyImage();
    } else {
      loader.onload = applyImage;
      loader.onerror = applyImage;
    }
  }

  const swatchesMore = card.querySelector('.hotspot-dialog__swatch-more');
  if (swatchesMore instanceof HTMLAnchorElement) {
    swatchesMore.href = variantUrl;
    if (button.dataset.variantId) swatchesMore.setAttribute('data-variant-id', button.dataset.variantId);
    swatchesMore.setAttribute('data-variant-url', variantUrl);
  }

  const quickAdd = card.querySelector('quick-add-component');
  if (quickAdd && variantUrl) {
    quickAdd.setAttribute('data-product-url', variantUrl);
  }

  const colorLine = card.querySelector('.hotspot-dialog__color-line');
  if (colorLine) {
    colorLine.textContent = button.dataset.variantSubtitle || '';
  }

  const priceOuter = card.querySelector('.hotspot-dialog__product-price');
  const oldPriceContainer = priceOuter ? priceOuter.querySelector('[ref="priceContainer"]') : null;
  if (button.dataset.priceHtml && oldPriceContainer) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = button.dataset.priceHtml;
    const newPrice = wrapper.querySelector('[ref="priceContainer"]') || wrapper.firstElementChild;
    if (newPrice) oldPriceContainer.replaceWith(newPrice);
  }

  if (commitActive) {
    card.querySelectorAll('.hotspot-dialog__swatch-button').forEach((swatchButton) => {
      swatchButton.setAttribute('aria-pressed', swatchButton === button ? 'true' : 'false');
      swatchButton.closest('.hotspot-dialog__swatch-item')?.classList.toggle('is-active', swatchButton === button);
    });
  }
};

const restoreActiveHotspotSwatchState = (card) => {
  if (!(card instanceof HTMLElement)) return;

  const activeButton =
    card.querySelector('.hotspot-dialog__swatch-button[aria-pressed="true"]') ||
    card.querySelector('.hotspot-dialog__swatch-item.is-active .hotspot-dialog__swatch-button');
  if (!(activeButton instanceof HTMLElement)) return;

  applyHotspotSwatchState(activeButton, { commitActive: false });
};

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('.hotspot-dialog__swatch-button') : null;
  if (!(button instanceof HTMLElement) || !button.dataset.variantUrl) return;

  event.preventDefault();
  event.stopPropagation();
  applyHotspotSwatchState(button, { commitActive: true });
});

document.addEventListener('pointerover', (event) => {
  if (!(event instanceof PointerEvent) || event.pointerType !== 'mouse') return;

  const button = event.target instanceof Element ? event.target.closest('.hotspot-dialog__swatch-button') : null;
  if (!(button instanceof HTMLElement) || !button.dataset.variantUrl) return;

  applyHotspotSwatchState(button, { commitActive: false });
});

document.addEventListener('pointerout', (event) => {
  if (!(event instanceof PointerEvent) || event.pointerType !== 'mouse') return;

  const button = event.target instanceof Element ? event.target.closest('.hotspot-dialog__swatch-button') : null;
  if (!(button instanceof HTMLElement) || !button.dataset.variantUrl) return;

  const nextButton =
    event.relatedTarget instanceof Element ? event.relatedTarget.closest('.hotspot-dialog__swatch-button') : null;
  if (nextButton instanceof HTMLElement && button.closest('.hotspot-dialog__card--product')?.contains(nextButton)) return;

  // Crossing the gap between swatches in the same list: keep the current
  // preview instead of snapping back to the active swatch and re-previewing.
  const swatchList = button.closest('.hotspot-dialog__swatches');
  if (swatchList && event.relatedTarget instanceof Element && swatchList.contains(event.relatedTarget)) return;

  restoreActiveHotspotSwatchState(button.closest('.hotspot-dialog__card--product'));
});

document.addEventListener('focusin', (event) => {
  const button = event.target instanceof Element ? event.target.closest('.hotspot-dialog__swatch-button') : null;
  if (!(button instanceof HTMLElement) || !button.dataset.variantUrl) return;

  applyHotspotSwatchState(button, { commitActive: false });
});

document.addEventListener('focusout', (event) => {
  const button = event.target instanceof Element ? event.target.closest('.hotspot-dialog__swatch-button') : null;
  if (!(button instanceof HTMLElement) || !button.dataset.variantUrl) return;

  const nextButton =
    event.relatedTarget instanceof Element ? event.relatedTarget.closest('.hotspot-dialog__swatch-button') : null;
  if (nextButton instanceof HTMLElement && button.closest('.hotspot-dialog__card--product')?.contains(nextButton)) return;

  restoreActiveHotspotSwatchState(button.closest('.hotspot-dialog__card--product'));
});
